import capitalize from "lodash/capitalize";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import queryString from "query-string";
import { reaction } from "mobx";
import { clipTypes, heroState, player as JumboPlayer } from "./event-hero";
import { getViewerCount } from "./live-ticker";
import { detect } from "detect-browser";

(function () {
  // escape hatch
  if (!document.body.classList.contains("single-event")) return;

  const userAgentData = getUserAgentData();

  // ? Actual Firebase login performed in visit-time.js
  // ? we just piggy back into the Firebase authStateChanged event
  firebase.auth().onAuthStateChanged(function (fb_user) {
    if (fb_user) {
      // ! Setup
      // Mutable state
      let state = {
        clipInterval: null,
        currentViewRef: null,
        currentViewToRestore: null,
        currentViewRefSiblings: null,
        liveStatusRef: null,
        droppedCurrentView: null,
        eventModeWatcherInitialized: null,
      };
      // Constants
      const database = firebase.database(),
        pageID = parseInt(WP.pageID),
        envKey = WP.envKey,
        eventRef = database.ref(`${envKey}/event${pageID}`),
        activityLogRef = database.ref(`${envKey}/activityLog`),
        clipViewsRef = database.ref(`${envKey}/clipViews`),
        clipChapterViewsRef = database.ref(`${envKey}/clipChapterViews`),
        userViewsRef = database.ref(`${envKey}/userViews`),
        user = parseInt(WP.uid)
          ? {
              uid: parseInt(WP.uid),
              niceName: WP.niceName,
              userAgentData: userAgentData,
            }
          : { uid: null, niceName: "anonymous" };

      // Setup anonymous users via localstorage
      if (!user.uid) {
        if (!localStorage.getItem("anonUid")) {
          const anonUid = `sesame${now()}`;
          localStorage.setItem("anonUid", anonUid);
        }
        user.anonUid = localStorage.getItem("anonUid");
      }

      // ! Base Functions
      function now() {
        return Math.floor(Date.now() / 1000);
      }
      function serverNow() {
        return firebase.database.ServerValue.TIMESTAMP;
      }

      function logActivity(data = {}) {
        if (!user.uid || !data.type) return;

        const core = {
          user,
          timestamp: now(),
        };
        const dataPoint = {
          ...core,
          ...data,
        };
        activityLogRef.push(dataPoint);
      }

      // ! Maintaining the currentlyViewing count
      function startLiveStatus() {
        // add to currentlyViewing list
        const liveStatusRef = eventRef.child("currentlyViewing").push();
        // ? instruct firebase on disconnection asap
        liveStatusRef.onDisconnect().remove();
        state.liveStatusRef = liveStatusRef;
        liveStatusRef.update({
          ...user,
          timestamp: serverNow(),
        });

        // maybe update maxConcurrent (firebase transaction)
        eventRef.child("currentlyViewing").once("value", (snap) => {
          const data = snap.val();
          const numberViewing = getViewerCount(data);
          const counter = eventRef.child("currentlyViewing/maxConcurrent");

          counter.transaction((currentCount) => {
            if (!currentCount || numberViewing > currentCount)
              return numberViewing;
          });
        });

        // log to activity log
        logActivity({
          type: "roomJoin",
          roomId: pageID,
        });
        const pendingLeaveLog = activityLogRef.push();
        pendingLeaveLog.onDisconnect().set({
          user,
          timestamp: serverNow(),
          type: "roomLeave",
          roomId: pageID,
        });
      }

      // ! Tracking static views (images, pdfs, links, and upcoming/archived events)
      // ? "main ref" refers to the event-nested location of the view
      // ? views also get recorded to "sibling" trees such as "clipViews" and "userViews"
      function trackStaticView({ mainRef, type }) {
        if (!user.uid) return;

        // setup data
        const view = {
          ...user,
          type,
          eid: pageID,
          eventMode: heroState.mode,
          timestamp: now(),
        };

        // add clip data
        if (type !== "event") {
          view.clipId = parseInt(mainRef.parent.key);
        }

        // record the view to main ref
        mainRef.push(view);

        // "fan-out" to userViews and clipViews
        userViewsRef.child(view.uid).push(view);
        if (type !== "event") {
          clipViewsRef.child(view.clipId).push(view);
        }

        // also activity log it
        // ? don't activity log an upcoming/archived event "view",
        // ? since this just duplicates the page load activity log
        if (view.clipId) {
          logActivity({
            type: `${type}View`,
            clipId: view.clipId,
            roomId: pageID,
          });
        }
      }

      // ! Tracking view starts and ends (events and clips)
      // ? "main ref" refers to the event-nested location of the view
      // ? views also get recorded to "sibling" trees such as "clipViews" and "userViews"
      function trackViewStart({ mainRef, type }) {
        //! only track anons for event views
        if (type !== "event" && !user.uid) return;

        const view = {
          ...user,
          type,
          eid: pageID,
          eventMode: heroState.mode,
          startAt: now(),
        };
        // add clip data
        if (type === "clip" || type === "audioClip") {
          view.clipId = parseInt(mainRef.parent.key);
        }

        // record the view to main ref
        const key = mainRef.push(view).key;
        state.currentViewRef = mainRef.child(key);
        state.currentViewToRestore = { mainRef, type };

        // tell firebase server how to handle disconnections
        state.currentViewRef.onDisconnect().update({ endAt: serverNow() });

        // "fan-out" to userViews and clipViews if applicable
        //! only non-anon users fan-out
        if (user.uid) {
          const userViewKey = userViewsRef.child(view.uid).push(view).key,
            userViewRef = userViewsRef.child(`${view.uid}/${userViewKey}`);
          state.currentViewRefSiblings = [userViewRef];
          if (type !== "event") {
            const clipViewKey = clipViewsRef.child(view.clipId).push(view).key,
              clipViewRef = clipViewsRef.child(`${view.clipId}/${clipViewKey}`);
            state.currentViewRefSiblings.push(clipViewRef);
          }
          // also tell firebase server how to handle disconnections on siblings
          state.currentViewRefSiblings.forEach((ref) =>
            ref.onDisconnect().update({ endAt: serverNow() })
          );
        }

        // activity log it
        if (type === "clip") {
          logActivity({
            type: "clipView",
            clipId: view.clipId,
            roomId: pageID,
          });
        }
        if (type === "audioClip") {
          logActivity({
            type: "clipListen",
            clipId: view.clipId,
            roomId: pageID,
          });
        }
      }
      function trackViewEnd() {
        if (!user.uid) return;

        const timestamp = now();
        // update endAt time on main ref
        state.currentViewRef.update({ endAt: timestamp });
        state.currentViewRef.onDisconnect().cancel();

        // fan out
        if (state.currentViewRefSiblings.length) {
          state.currentViewRefSiblings.forEach((ref) => {
            ref.update({ endAt: timestamp });
            ref.onDisconnect().cancel();
          });
        }

        // clear state
        state.currentViewRef = null;
        state.currentViewRefSiblings = null;
        if (state.clipInterval) {
          clearInterval(state.clipInterval);
          state.clipInterval = null;
        }
      }

      /**
       * Track Clip
       *
       * Helper to call the various base functions required for tracking clips
       * of various types
       *
       * @param {string} type
       * @param {string|int} clipId
       *
       * @returns {void}
       */
      function trackClip(type, clipId) {
        switch (type) {
          case "vimeo":
          case "youtube":
            trackViewStart({
              type: "clip",
              mainRef: eventRef.child(`clips/${clipId}/views`),
            });
            trackClipChapters(clipId);
            listenForClipEnd();
            break;

          case "wavesurfer":
            trackViewStart({
              type: "audioClip",
              mainRef: eventRef.child(`audioClips/${clipId}/listens`),
            });
            trackAudioClipChapters(clipId);
            listenForAudioClipEnd();
            break;

          case "pdf":
            trackStaticView({
              type: "pdf",
              mainRef: eventRef.child(`pdfs/${clipId}/views`),
            });
            break;

          case "image":
            trackStaticView({
              type: "image",
              mainRef: eventRef.child(`images/${clipId}/views`),
            });
            break;
        }
      }

      // ! Dealing with chapters
      function countEventChapterView(viewsRef) {
        if (!user.uid) return;

        viewsRef.once("value").then(function (snapshot) {
          let views = snapshot.val() || {};
          const viewsArr = Object.values(views);
          if (!viewsArr.some((node) => node.uid == user.uid)) {
            viewsRef.push(user);
          }
        });
      }
      function countClipChapterView({ mainRef, clipId, chapterGuid, type }) {
        if (!user.uid) return;

        mainRef.once("value").then(function (snapshot) {
          const chapter = snapshot.val() || {},
            views = chapter[`${type}s`] || {},
            viewsArr = Object.values(views),
            startAt = chapter.startAt || null,
            title = chapter.title || null;

          if (!viewsArr.some((node) => node.uid == user.uid)) {
            // push main views ref (event nested)
            mainRef.child(`${type}s`).push(user);

            // fan-out to clipChapterViews (not for anon views)
            if (!!user.uid) {
              // ensure title and StartAt are set
              clipChapterViewsRef.child(`${clipId}/${chapterGuid}`).update({
                startAt,
                title,
              });
              // push view
              clipChapterViewsRef
                .child(`${clipId}/${chapterGuid}/views`)
                .push(user);
            }
          }
        });
      }
      function trackClipChapters(clipId) {
        state.clipInterval = setInterval(() => {
          let currentChapter = null;

          function findAndTrackCurrentChapter(seconds) {
            eventRef
              .child(`clips/${clipId}/chapters`)
              .orderByChild("startAt")
              .once("value", function (chapters) {
                chapters.forEach((chapter) => {
                  if (chapter.val().startAt <= seconds)
                    currentChapter = chapter.key;
                });
              })
              .then(() => {
                if (currentChapter)
                  countClipChapterView({
                    mainRef: eventRef.child(
                      `clips/${clipId}/chapters/${currentChapter}`
                    ),
                    clipId,
                    chapterGuid: currentChapter,
                    type: "view",
                  });
              });
          }

          if (heroState.currentType === "vimeo") {
            JumboPlayer.getCurrentTime().then((seconds) =>
              findAndTrackCurrentChapter(seconds)
            );
          }
          if (heroState.currentType === "youtube") {
            const seconds = JumboPlayer.getCurrentTime();
            findAndTrackCurrentChapter(seconds);
          }
        }, 5000);
      }
      function trackAudioClipChapters(clipId) {
        state.clipInterval = setInterval(() => {
          let currentChapter = null;
          let currentTime = JumboPlayer.getCurrentTime();
          eventRef
            .child(`audioClips/${clipId}/chapters`)
            .orderByChild("startAt")
            .once("value", function (chapters) {
              chapters.forEach((chapter) => {
                if (chapter.val().startAt <= currentTime)
                  currentChapter = chapter.key;
              });
            })
            .then(() => {
              if (currentChapter)
                countClipChapterView({
                  mainRef: eventRef.child(
                    `audioClips/${clipId}/chapters/${currentChapter}`
                  ),
                  clipId,
                  chapterGuid: currentChapter,
                  type: "listen",
                });
            });
        }, 5000);
      }

      // ! Listening for clip ends
      function listenForClipEnd() {
        if (heroState.currentType === "vimeo") {
          JumboPlayer.off("ended");
          setTimeout(() => {
            JumboPlayer.on("ended", () => {
              if (state.currentViewRef) trackViewEnd();
            });
          }, 1000);
        }
        if (heroState.currentType === "youtube") {
          JumboPlayer.on("ended", () => {
            if (state.currentViewRef) trackViewEnd();
          });
        }
      }
      function listenForAudioClipEnd() {
        JumboPlayer.off("finish");
        JumboPlayer.on("finish", () => {
          if (state.currentViewRef) trackViewEnd();
        });
      }

      // ! Event Handlers

      // On Load: track event
      // * Live event: start recording on pageload, even if plastic wrap is still on
      if (
        heroState.mode === "live" &&
        !clipTypes.includes(heroState.currentType) //? in case clip accessed directly
      ) {
        trackViewStart({ type: "event", mainRef: eventRef.child("views") });
      }

      // * Upcoming or Archived event: track a "static view" without tracking time
      // ? current request from client is that users who load the page in
      // ? upcoming or archived mode should be shown as attendees on an event,
      // ? but they shouldn't accrue any watch time.
      if (["upcoming", "archived"].includes(heroState.mode)) {
        trackStaticView({ type: "event", mainRef: eventRef.child("views") });
      }

      // On Load: track clip
      const { c: initialClipId } = queryString.parse(window.location.search);
      if (initialClipId && heroState.media?.wpId === initialClipId) {
        trackClip(heroState.media.type, initialClipId);
      }

      // User clicks link that opens clip in new window
      const clipLinks = document.querySelectorAll(".clip-link");
      if (clipLinks.length) {
        for (const clipLink of clipLinks) {
          clipLink.addEventListener("click", function () {
            let clipId = clipLink.getAttribute("data-clip-id");
            let mql = window.matchMedia("screen and (max-device-width: 960px)");

            // Link clip clicks
            if (clipLink.classList.contains("type-link")) {
              trackStaticView({
                type: "link",
                mainRef: eventRef.child(`links/${clipId}/views`),
              });
            }

            // PDF clip clicks (if opening in new window)
            if (
              clipLink.classList.contains("type-pdf") &&
              (mql.matches || document.body.classList.contains("live-mode"))
            ) {
              trackStaticView({
                type: "pdf",
                mainRef: eventRef.child(`pdfs/${clipId}/views`),
              });
            }
          });
        }
      }

      // On hero state change
      reaction(
        () => heroState.media,
        (nextMedia, prevMedia) => {
          if (isEqual(nextMedia, prevMedia)) return;
          handleHeroSwitch(nextMedia);
        }
      );
      /**
       * Handle hero switch -- updates trackers
       *
       * @param {Object} nextMedia
       * @param {String} nextMedia.type
       * @param {String} nextMedia.id
       * @returns {void}
       */
      function handleHeroSwitch(nextMedia) {
        // cleanup
        if (state.currentViewRef) trackViewEnd();

        // live media
        if (nextMedia.id === heroState.liveMedia?.id) {
          trackViewStart({
            type: "event",
            mainRef: eventRef.child("views"),
          });
          return;
        }

        // clip (by type)
        trackClip(nextMedia.type, nextMedia.wpId);
      }

      // Event goes live / Viewer joins live event / Event ends
      eventRef.child("liveNow").on("value", function (snapshot) {
        // handle event end coming through firebase (admin saves event with non-live mode)
        if (state.eventModeWatcherInitialized && !!state.currentViewRef) {
          state.currentViewRef.once("value").then((snap) => {
            const currentViewType = get(snap.val(), "type", null);
            // watching event: end current view
            if (currentViewType === "event") trackViewEnd();
            // watching a clip/image/etc: quietly remove the "return to the show" button
            if (currentViewType && currentViewType !== "event")
              document.querySelector(
                '.hero-infobar [href="#live"]'
              ).style.display = "none";
          });
        }
        state.eventModeWatcherInitialized = true;
      });

      // Event chapter(s) start & end
      eventRef.child("chapters").once("value", (snap) => {
        const data = snap.val() || {};
        Object.keys(data).forEach((chapterGuid) => {
          eventRef
            .child(`chapters/${chapterGuid}/liveNow`)
            .on("value", (snap) => {
              const liveNow = snap.val() || false;
              if (liveNow) {
                countEventChapterView(
                  eventRef.child(`chapters/${chapterGuid}/views`)
                );
              }
            });
        });
      });

      // Presence
      const connectedRef = database.ref(".info/connected");
      let isInitialized = false;
      connectedRef.on("value", (snap) => {
        if (snap.val() === true) {
          // connected or re-connected: add to currentlyViewing
          startLiveStatus();

          if (state.droppedCurrentView) {
            // re-connected, try to restore tracking
            trackViewStart(state.droppedCurrentView);
            state.droppedCurrentView = null;
          }
        } else {
          if (isInitialized) {
            // disconnected, update client state for disconnected status
            if (state.currentViewRef) {
              state.droppedCurrentView = state.currentViewToRestore;
            }
            if (state.liveStatusRef) {
              state.liveStatusRef.remove();
            }
          }
        }

        isInitialized = true;
      });

      // Correct Presence for Idle tabs
      // ? long term idle tabs (non focused tabs) create an issue for us:
      // ? their jwt auth tokens expire without getting refreshed because the tab is not focused
      // ? firebase's server function .onDisconnect() _checks_ the token, so it only fires if the token is still valid
      // ? this means presence can't be accurately measured on tabs that may have been open more than an hour, and idle
      // solution is to disconnect manually after 10 minutes of idling, when the token is still valid
      // and reconnect if they come back
      // ? an alternative would be manually refreshing tokens on some interval (less than 1hr) when tab is not focused
      let idleTimer;
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          database.goOnline();
          if (idleTimer) {
            window.clearTimeout(idleTimer);
            idleTimer = undefined;
          }
        } else {
          //? refresh token to ensure it will still be valid in 10 mins
          firebase.auth().currentUser.getIdToken(true);
          //? kill firebase connection after ten minutes off tab
          idleTimer = setTimeout(() => {
            database.goOffline();
          }, 10 * 60 * 1000);
        }
      });
    }
  });
})();

// Get user device / browser / OS info
function getUserAgentData() {
  const browser = detect();
  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "Tablet";
    }
    if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    ) {
      return "Mobile";
    }
    return "Desktop";
  };
  return {
    device: getDeviceType(),
    browser: capitalize(browser.name),
    os: browser.os,
  };
}
